'use client';

import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import type * as RPNInput from 'react-phone-number-input';

import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

interface ChangePhoneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPhone?: string | null;
  onVerified: (phone: string) => void;
}

export function ChangePhoneDialog({
  open,
  onOpenChange,
  currentPhone,
  onVerified,
}: ChangePhoneDialogProps) {
  const [newPhone, setNewPhone] = useState<RPNInput.Value | undefined>();
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');

  useEffect(() => {
    if (!open) {
      setNewPhone(undefined);
      setOtpSent(false);
      setOtpSending(false);
      setOtpValue('');
      setOtpVerifying(false);
      setOtpVerified(false);
      setMaskedPhone('');
    }
  }, [open]);

  const handleSendOtp = async () => {
    if (!newPhone || newPhone.length < 10) {
      toast.error('Enter a valid phone number');
      return;
    }

    setOtpSending(true);
    setOtpVerified(false);
    try {
      const res = await axiosInstance.post('/api/user/phone/otp', { phone: newPhone });
      setMaskedPhone(res.data.maskedPhone || '');
      setOtpSent(true);
      setOtpValue('');
      toast.success('OTP sent to your new phone number');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async (otp: string) => {
    if (otp.length !== 6 || !newPhone) return;
    setOtpVerifying(true);
    try {
      const res = await axiosInstance.post('/api/user/phone/otp/verify', {
        phone: newPhone,
        otp,
      });
      const verifiedPhone = (res.data.phone as string) || String(newPhone);
      const displayPhone = verifiedPhone.startsWith('+') ? verifiedPhone : `+${verifiedPhone}`;
      setOtpVerified(true);
      toast.success('Phone number verified and updated');
      onVerified(displayPhone);
      onOpenChange(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Invalid OTP');
      setOtpVerified(false);
    } finally {
      setOtpVerifying(false);
    }
  };

  const currentDisplay = currentPhone
    ? currentPhone.startsWith('+')
      ? currentPhone
      : `+${currentPhone}`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md z-[200]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            Change Phone Number
          </DialogTitle>
          <DialogDescription>
            Enter your new number. We&apos;ll send an OTP via WhatsApp to verify it.
            {currentDisplay ? (
              <>
                {' '}
                Current number: <span className="font-mono text-foreground">{currentDisplay}</span>
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="new-phone">New Phone Number</Label>
            <PhoneInput
              id="new-phone"
              placeholder="Enter new phone number"
              value={newPhone}
              onChange={(value) => {
                setNewPhone(value);
                if (otpSent || otpVerified) {
                  setOtpSent(false);
                  setOtpVerified(false);
                  setOtpValue('');
                  setMaskedPhone('');
                }
              }}
              disabled={otpSending || otpVerifying}
              defaultCountry="IN"
            />
          </div>

          {otpSent &&
            (otpVerified ? (
              <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  <span className="text-foreground font-medium">
                    Phone verified via OTP
                    {maskedPhone ? (
                      <>
                        {' — '}
                        <span className="font-mono">{maskedPhone}</span>
                      </>
                    ) : null}
                  </span>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Smartphone className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-foreground font-medium">
                    OTP sent to{' '}
                    {maskedPhone ? (
                      <span className="font-mono">{maskedPhone}</span>
                    ) : (
                      'your new phone'
                    )}
                  </span>
                </div>
                <div className="space-y-1.5">
                  <Label>Enter OTP</Label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={6}
                      value={otpValue}
                      onChange={(val) => {
                        setOtpValue(val);
                        if (val.length === 6 && !otpVerified) {
                          handleVerifyOtp(val);
                        }
                      }}
                      disabled={otpVerifying}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                {otpVerifying && (
                  <p className="text-xs text-primary font-medium flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Verifying...
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setOtpValue('');
                    handleSendOtp();
                  }}
                  disabled={otpSending || otpVerifying}
                  className="text-xs text-primary font-medium hover:underline disabled:opacity-50"
                >
                  {otpSending ? 'Resending...' : 'Resend OTP'}
                </button>
              </div>
            ))}
        </div>

        {!otpSent ? (
          <DialogFooter>
            <Button
              type="button"
              onClick={handleSendOtp}
              disabled={otpSending || !newPhone || newPhone.length < 10}
            >
              {otpSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Sending OTP...
                </>
              ) : (
                <>
                  <Smartphone className="h-4 w-4 mr-2" />
                  Send OTP
                </>
              )}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
